
import { useLanguage } from '../../../hooks/useLanguage';
import { portfolio } from '../../../mocks/ru/common';
import { useState, useMemo } from 'react';

const Portfolio = () => {
  const { currentLanguage } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const getTexts = () => {
    switch (currentLanguage) {
      case 'fr':
        return {
          title: 'Nos Réalisations',
          subtitle: 'Découvrez quelques-uns de nos projets récents dans les cantons de Vaud et Genève',
          viewAll: 'Voir tous les projets',
          all: 'Tous',
          categories: {
            'Finitions intérieures': 'Finitions intérieures',
            'Peinture et revêtements': 'Peinture et revêtements',
            'Revêtements de sol': 'Revêtements de sol',
            'Façades et isolation': 'Façades et isolation',
            'Projets spéciaux': 'Projets spéciaux'
          }
        };
      case 'en':
        return {
          title: 'Our Portfolio',
          subtitle: 'Discover some of our recent projects in Vaud and Geneva cantons',
          viewAll: 'View all projects',
          all: 'All',
          categories: {
            'Interior Finishing': 'Interior Finishing',
            'Painting & Coatings': 'Painting & Coatings',
            'Floor Coverings': 'Floor Coverings',
            'Facades & Insulation': 'Facades & Insulation',
            'Special Projects': 'Special Projects'
          }
        };
      case 'ro':
        return {
          title: 'Portofoliul Nostru',
          subtitle: 'Descoperiți câteva dintre proiectele noastre recente din cantonele Vaud și Geneva',
          viewAll: 'Vezi toate proiectele',
          all: 'Toate',
          categories: {
            'Finisaje interioare': 'Finisaje interioare',
            'Vopsitorie și acoperiri': 'Vopsitorie și acoperiri',
            'Pardoseli': 'Pardoseli',
            'Fațade și izolație': 'Fațade și izolație',
            'Proiecte speciale': 'Proiecte speciale'
          }
        };
      default:
        return {
          title: 'Наше Портфолио',
          subtitle: 'Ознакомьтесь с некоторыми из наших недавних проектов в кантонах Во и Женева',
          viewAll: 'Посмотреть все проекты',
          all: 'Все',
          categories: {
            'Внутренняя отделка': 'Внутренняя отделка',
            'Покраска и покрытия': 'Покраска и покрытия',
            'Напольные покрытия': 'Напольные покрытия',
            'Фасады и утепление': 'Фасады и утепление',
            'Специальные проекты': 'Специальные проекты'
          }
        };
    }
  };

  const texts = getTexts();

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(portfolio.map(p => p.category)));
    return uniqueCategories;
  }, []);

  const filteredProjects = useMemo(() => {
    if (selectedCategory === 'all') {
      return portfolio;
    }
    return portfolio.filter(project => project.category === selectedCategory);
  }, [selectedCategory]);

  return (
    <section id="portfolio" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {texts.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {texts.subtitle}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
              selectedCategory === 'all'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {texts.all}
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {texts.categories[category as keyof typeof texts.categories] || category}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div key={project.id} className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-72 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-6 group-hover:translate-y-0 transition-transform duration-300">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="bg-blue-600 text-xs px-3 py-1 rounded-full font-medium">
                      {project.category}
                    </span>
                    <span className="text-sm opacity-90 flex items-center">
                      <i className="ri-map-pin-line mr-1"></i>
                      {project.location}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{project.title}</h3>
                  <p className="text-sm opacity-90 line-clamp-2">{project.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No projects found in this category</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Portfolio;
